import { createContext, useContext, useMemo, type ComponentType, type ReactNode } from "react";
import { hasRewardsPermission, type RewardsPermission } from "@ticketon/rewards-core";
import { identityTranslator, type Translator } from "../types/nav";
import { formatDisplayDate, formatDisplayDateTime } from "../util/dates";
import type { IErrorStateProps } from "../shared/states";

/** Severity of a host notification (maps onto the host's toast types). */
export type RewardsNotificationType = "success" | "error";

/**
 * Mirrors React Router's blocker: `isBlocked` is true while a navigation is held
 * back, `proceed` lets it through and `reset` cancels it.
 */
export interface IRewardsNavigationBlocker {
  isBlocked: boolean;
  proceed: () => void;
  reset: () => void;
}

/**
 * Services the host application injects into every rewards admin screen. The
 * package stays router- and toolkit-agnostic: navigation guards, toasts, error
 * panels and date formatting all come from the host.
 */
export interface IRewardsAdminHost {
  /** Translator (e.g. react-i18next `t`). */
  t: Translator;
  /** The actor's granted permission codes (wildcards allowed). */
  permissions: readonly string[];
  /** Shows a transient notification (toast). */
  notify: (message: string, type: RewardsNotificationType) => void;
  /**
   * Hook that blocks in-app navigation whenever `shouldBlock()` returns true at
   * navigation time (React Router's `useBlocker`). Must be a stable hook
   * reference for the provider's lifetime.
   */
  useNavigationBlocker: (shouldBlock: () => boolean) => IRewardsNavigationBlocker;
  /** Formats an ISO date (date-only or date-time) for display. */
  formatDate: (iso: string) => string;
  /** Formats an ISO date-time with hours and minutes for display. */
  formatDateTime: (iso: string) => string;
  /** Host error panel, rendered instead of the package fallback when provided. */
  ErrorState?: ComponentType<IErrorStateProps>;
}

const NOT_BLOCKED: IRewardsNavigationBlocker = { isBlocked: false, proceed: () => {}, reset: () => {} };

function useNoNavigationBlocker(): IRewardsNavigationBlocker {
  return NOT_BLOCKED;
}

const DEFAULT_HOST: IRewardsAdminHost = {
  t: identityTranslator,
  permissions: [],
  notify: () => {},
  useNavigationBlocker: useNoNavigationBlocker,
  formatDate: formatDisplayDate,
  formatDateTime: formatDisplayDateTime,
};

const RewardsAdminHostContext = createContext<IRewardsAdminHost>(DEFAULT_HOST);

export interface IRewardsAdminHostProviderProps extends Partial<IRewardsAdminHost> {
  children: ReactNode;
}

/** Injects the host services used by every rewards admin screen below it. */
export function RewardsAdminHostProvider({ children, ...overrides }: IRewardsAdminHostProviderProps) {
  const { t, permissions, notify, useNavigationBlocker, formatDate, formatDateTime, ErrorState } = overrides;
  const value = useMemo<IRewardsAdminHost>(
    () => ({
      t: t ?? DEFAULT_HOST.t,
      permissions: permissions ?? DEFAULT_HOST.permissions,
      notify: notify ?? DEFAULT_HOST.notify,
      useNavigationBlocker: useNavigationBlocker ?? DEFAULT_HOST.useNavigationBlocker,
      formatDate: formatDate ?? DEFAULT_HOST.formatDate,
      formatDateTime: formatDateTime ?? DEFAULT_HOST.formatDateTime,
      ErrorState,
    }),
    [t, permissions, notify, useNavigationBlocker, formatDate, formatDateTime, ErrorState],
  );
  return <RewardsAdminHostContext.Provider value={value}>{children}</RewardsAdminHostContext.Provider>;
}

/** Screen-level overrides: explicit props win over the provider. */
export interface IRewardsAdminHostOverrides {
  t?: Translator;
  permissions?: readonly string[];
}

export interface IRewardsAdmin extends IRewardsAdminHost {
  /** Whether the actor holds `required` (fail-closed when no permissions are known). */
  can: (required: RewardsPermission) => boolean;
}

/**
 * The host services for a screen, with the screen's own `t` / `permissions`
 * props taking precedence over the provider.
 */
export function useRewardsAdmin(overrides: IRewardsAdminHostOverrides = {}): IRewardsAdmin {
  const host = useContext(RewardsAdminHostContext);
  const t = overrides.t ?? host.t;
  const permissions = overrides.permissions ?? host.permissions;
  return useMemo(
    () => ({
      ...host,
      t,
      permissions,
      can: (required: RewardsPermission) => hasRewardsPermission(permissions, required),
    }),
    [host, t, permissions],
  );
}
